import { useState } from "react";
import type { ButtonHTMLAttributes } from "react";
import { IconButton } from "./icon-button";
import { CloseIcon, PlusIcon } from "./icons";
import type { ComponentSize, ComponentTone, ComponentVariant } from "./types";

type CollapsibleStateProps = {
  collapsible?: boolean;
  defaultExpanded?: boolean;
};

function getDefaultExpanded() {
  if (typeof window === "undefined") {
    return true;
  }

  return window.innerWidth > 430;
}

type CollapsibleToggleProps = {
  expanded: boolean;
  expandLabel: string;
  collapseLabel: string;
  className?: string;
  onClick: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  size?: ComponentSize;
  tone?: ComponentTone;
  variant?: ComponentVariant;
};

export function useCollapsibleState({
  collapsible = false,
  defaultExpanded,
}: CollapsibleStateProps) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? getDefaultExpanded());

  return {
    collapsible,
    expanded,
    setExpanded,
    toggleExpanded: () => setExpanded((current) => !current),
  };
}

export function CollapsibleToggle({
  expanded,
  expandLabel,
  collapseLabel,
  className,
  onClick,
  size = "sm",
  tone = "neutral",
  variant = "outline",
}: CollapsibleToggleProps) {
  return (
    <IconButton
      className={className}
      icon={expanded ? <CloseIcon /> : <PlusIcon />}
      label={expanded ? collapseLabel : expandLabel}
      size={size}
      tone={tone}
      variant={variant}
      aria-expanded={expanded}
      onClick={onClick}
    />
  );
}
